import type { ChecklistSchema } from "../../studio/schemas/checklist-schema.js";

const template = document.createElement("template");
template.innerHTML = `
  <div style="display: flex; height: 100%; width: 100%;">
    <main style="flex: 1; padding: var(--space-6); overflow-y: auto; background: var(--color-graphite-50);">
      <div id="canvas" style="width: 100%; max-width: 800px; margin: 0 auto; background: var(--color-surface-primary); min-height: 100%; border: 1px solid var(--color-graphite-300); border-radius: var(--radius-md); padding: var(--space-8); box-shadow: var(--shadow-sm);">
        <h2 style="font-size: var(--font-size-lg); color: var(--color-text-muted); margin-bottom: var(--space-4);">Checklist Groups</h2>
        <div id="groups-container"></div>
        <button id="add-group-btn" style="margin-top: var(--space-4); background: var(--color-surface-primary); color: var(--ia-primary); border: 1px dashed var(--color-primary); padding: var(--space-3); width: 100%; border-radius: var(--radius-md); cursor: pointer; font-weight: 500;">+ Add Group</button>
      </div>
    </main>
    <aside style="width: 300px; background: var(--color-surface-primary); border-left: 1px solid var(--color-graphite-300); padding: var(--space-4); overflow-y: auto;">
      <h3 style="font-size: var(--font-size-sm); color: var(--color-text-secondary); text-transform: uppercase; margin-bottom: var(--space-3);">Properties</h3>
      <div id="properties-panel">
        <p style="color: var(--color-graphite-400); font-size: var(--font-size-sm);">Select a group or component to edit properties.</p>
      </div>
    </aside>
  </div>
`;

export class ChecklistBuilder extends HTMLElement {
  private schema: ChecklistSchema = { groups: [] };
  private selectedItemId: string | null = null;
  private selectedItemType: 'GROUP' | 'COMPONENT' | null = null;
  private onSchemaChangeCallback: ((schema: ChecklistSchema) => void) | null = null;

  constructor() {
    super();
    this.appendChild(template.content.cloneNode(true));
  }

  public connectedCallback() {
    this.querySelector("#add-group-btn")?.addEventListener("click", () => {
      this.schema.groups.push({
        id: crypto.randomUUID(),
        code: "group_" + Date.now().toString().slice(-6),
        name: "New Group",
        order: this.schema.groups.length,
        components: []
      });
      this.notifyChange();
      this.renderCanvas();
    });
    this.renderCanvas();
  }

  public setSchema(schema: ChecklistSchema) {
    this.schema = JSON.parse(JSON.stringify(schema));
    if (!this.schema.groups) this.schema.groups = [];
    this.renderCanvas();
    this.renderProperties();
  }

  public onSchemaChange(callback: (schema: ChecklistSchema) => void) {
    this.onSchemaChangeCallback = callback;
  }

  private notifyChange() {
    if (this.onSchemaChangeCallback) {
      this.onSchemaChangeCallback(JSON.parse(JSON.stringify(this.schema)));
    }
  }

  private renderCanvas() {
    const container = this.querySelector("#groups-container")!;
    container.innerHTML = "";

    this.schema.groups.forEach(group => {
      const groupEl = document.createElement("div");
      const isGroupSelected = this.selectedItemId === group.id && this.selectedItemType === 'GROUP';
      
      groupEl.style.cssText = `
        border: 1px solid ${isGroupSelected ? 'var(--color-primary)' : 'var(--color-graphite-300)'};
        border-radius: var(--radius-md);
        margin-bottom: var(--space-4);
        overflow: hidden;
      `;

      groupEl.innerHTML = `
        <div class="group-header" style="background: ${isGroupSelected ? 'rgba(59, 130, 246, 0.05)' : 'var(--color-graphite-50)'}; padding: var(--space-3); cursor: pointer; display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid var(--color-graphite-300);">
          <strong style="font-size: var(--font-size-md); color: var(--color-text-muted);">${group.name}</strong>
          <span style="font-size: var(--font-size-xs); color: var(--color-text-secondary);">${group.code}</span>
        </div>
        <div class="components-container" style="padding: var(--space-3); background: var(--color-surface-primary);">
          ${group.components.map(comp => {
            const isCompSelected = this.selectedItemId === comp.id && this.selectedItemType === 'COMPONENT';
            return `
              <div class="component-item" data-id="${comp.id}" style="padding: var(--space-2); margin-bottom: var(--space-2); border: 1px solid ${isCompSelected ? 'var(--color-primary)' : 'var(--color-graphite-300)'}; border-radius: var(--radius-sm); cursor: pointer; background: ${isCompSelected ? 'rgba(59, 130, 246, 0.05)' : 'white'};">
                <div style="font-size: var(--font-size-sm); color: var(--color-text-muted);">${comp.name}</div>
                <div style="font-size: var(--font-size-xs); color: var(--color-text-secondary);">${comp.code}</div>
              </div>
            `;
          }).join('')}
          <button class="add-comp-btn" data-group-id="${group.id}" style="margin-top: var(--space-2); background: none; color: var(--ia-primary); border: none; font-size: var(--font-size-sm); cursor: pointer; padding: 0;">+ Add Component</button>
        </div>
      `;

      groupEl.querySelector(".group-header")?.addEventListener("click", () => {
        this.selectedItemId = group.id;
        this.selectedItemType = 'GROUP';
        this.renderCanvas();
        this.renderProperties();
      });

      groupEl.querySelectorAll(".component-item").forEach(item => {
        item.addEventListener("click", (e) => {
          e.stopPropagation();
          this.selectedItemId = (item as HTMLElement).dataset['id']!;
          this.selectedItemType = 'COMPONENT';
          this.renderCanvas();
          this.renderProperties();
        });
      });

      groupEl.querySelector(".add-comp-btn")?.addEventListener("click", (e) => {
        e.stopPropagation();
        group.components.push({
          id: crypto.randomUUID(),
          code: "comp_" + Date.now().toString().slice(-6),
          name: "New Component",
          order: group.components.length,
          required: true,
          photoEnabled: false,
          photoRequired: false,
          multiplePhotos: false,
          commentEnabled: false,
          commentRequired: false,
          recommendationEnabled: false
        });
        this.notifyChange();
        this.renderCanvas();
      });

      container.appendChild(groupEl);
    });
  }

  private renderProperties() {
    const panel = this.querySelector("#properties-panel")!;
    
    if (!this.selectedItemId) {
      panel.innerHTML = `<p style="color: var(--color-graphite-400); font-size: var(--font-size-sm);">Select a group or component to edit properties.</p>`;
      return;
    }

    if (this.selectedItemType === 'GROUP') {
      const group = this.schema.groups.find(g => g.id === this.selectedItemId);
      if (group) {
        panel.innerHTML = `
          <div style="display: flex; flex-direction: column; gap: var(--space-3);">
            <div><label style="font-size: var(--font-size-xs);">Name</label><input type="text" id="prop-name" value="${group.name}" style="width:100%; padding: 4px;"></div>
            <div><label style="font-size: var(--font-size-xs);">Code</label><input type="text" id="prop-code" value="${group.code}" style="width:100%; padding: 4px;"></div>
            <button id="prop-delete" style="color: red; margin-top: 10px;">Delete Group</button>
          </div>
        `;
        panel.querySelector("#prop-name")?.addEventListener("input", (e) => { group.name = (e.target as HTMLInputElement).value; this.notifyChange(); this.renderCanvas(); });
        panel.querySelector("#prop-code")?.addEventListener("input", (e) => { group.code = (e.target as HTMLInputElement).value; this.notifyChange(); this.renderCanvas(); });
        panel.querySelector("#prop-delete")?.addEventListener("click", () => {
          this.schema.groups = this.schema.groups.filter(g => g.id !== group.id);
          this.selectedItemId = null;
          this.notifyChange();
          this.renderCanvas();
          this.renderProperties();
        });
      }
    } else {
      let component = null;
      let parentGroup = null;
      for (const g of this.schema.groups) {
        const c = g.components.find(comp => comp.id === this.selectedItemId);
        if (c) {
          component = c;
          parentGroup = g;
          break;
        }
      }
      
      if (component && parentGroup) {
        panel.innerHTML = `
          <div style="display: flex; flex-direction: column; gap: var(--space-3);">
            <div><label style="font-size: var(--font-size-xs);">Name</label><input type="text" id="prop-name" value="${component.name}" style="width:100%; padding: 4px;"></div>
            <div><label style="font-size: var(--font-size-xs);">Code</label><input type="text" id="prop-code" value="${component.code}" style="width:100%; padding: 4px;"></div>
            <div><label style="font-size: var(--font-size-xs);"><input type="checkbox" id="prop-req" ${component.required ? 'checked' : ''}> Required</label></div>
            <div><label style="font-size: var(--font-size-xs);"><input type="checkbox" id="prop-photo" ${component.photoEnabled ? 'checked' : ''}> Photo Enabled</label></div>
            <button id="prop-delete" style="color: red; margin-top: 10px;">Delete Component</button>
          </div>
        `;
        panel.querySelector("#prop-name")?.addEventListener("input", (e) => { component.name = (e.target as HTMLInputElement).value; this.notifyChange(); this.renderCanvas(); });
        panel.querySelector("#prop-code")?.addEventListener("input", (e) => { component.code = (e.target as HTMLInputElement).value; this.notifyChange(); this.renderCanvas(); });
        panel.querySelector("#prop-req")?.addEventListener("change", (e) => { component.required = (e.target as HTMLInputElement).checked; this.notifyChange(); });
        panel.querySelector("#prop-photo")?.addEventListener("change", (e) => { component.photoEnabled = (e.target as HTMLInputElement).checked; this.notifyChange(); });
        
        panel.querySelector("#prop-delete")?.addEventListener("click", () => {
          parentGroup.components = parentGroup.components.filter(c => c.id !== component.id);
          this.selectedItemId = null;
          this.notifyChange();
          this.renderCanvas();
          this.renderProperties();
        });
      }
    }
  }
}
if (!customElements.get("app-checklist-builder")) {
  customElements.define("app-checklist-builder", ChecklistBuilder);
}

